/**
 * Runtime Configuration Loader
 * 
 * Fetches configuration from the backend API at runtime instead of using 
 * build-time VITE_* environment variables. This allows changing config
 * without rebuilding the container.
 */

let configCache = null;

/**
 * Fetch configuration from the backend API
 */
export async function fetchRuntimeConfig() {
  if (configCache) {
    return configCache;
  }

  try {
    // Detect /config endpoint from current location (same origin as the app)
    const configUrl = '/config';

    const response = await fetch(configUrl);
    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status}`);
    }

    const config = await response.json();

    configCache = config;
    return config;
  } catch (error) {
    console.error('Failed to load runtime config:', error);

    // Fallback to sensible defaults if /config endpoint is unavailable
    const fallbackConfig = {
      auth: {
        development: false,
        azure: {
          clientId: '',
          tenantId: '',
          authority: 'https://login.microsoftonline.com/',
          scope: '',
          redirectUri: window.location.origin
        }
      },
      api: {
        baseUrl: '/api/v1',
        timeout: 30000
      },
      paths: {
        basePath: '/'
      }
    };

    configCache = fallbackConfig;
    return fallbackConfig;
  }
}

/**
 * Create MSAL configuration from runtime config
 */
export function createMsalConfig(runtimeConfig) {
  const { auth } = runtimeConfig;
  
  return {
    auth: {
      clientId: auth.azure.clientId,
      authority: auth.azure.authority,
      redirectUri: auth.azure.redirectUri,
      postLogoutRedirectUri: auth.azure.redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          
          const logLevel = 0; // Errors only
          if (level > logLevel) return;
          
          switch (level) {
            case 0: // Error
              console.error('[MSAL]', message);
              break;
            case 1: // Warning
              console.warn('[MSAL]', message);
              break;
            case 2: // Info
              console.info('[MSAL]', message);
              break;
            case 3: // Verbose
              console.debug('[MSAL]', message);
              break;
          }
        },
        piiLoggingEnabled: false,
        logLevel: 0, // Errors only
      },
    },
  };
}

/**
 * Create login request from runtime config
 */
export function createLoginRequest(runtimeConfig) {
  const scopes = runtimeConfig.auth.azure.scope ? [runtimeConfig.auth.azure.scope] : [];
  const additionalScopes = ['openid', 'profile', 'email'];
  
  return {
    scopes: [...scopes, ...additionalScopes],
  };
}

/**
 * Create token request from runtime config
 */
export function createTokenRequest(runtimeConfig) {
  const scopes = runtimeConfig.auth.azure.scope ? [runtimeConfig.auth.azure.scope] : undefined;
  
  return {
    scopes,
  };
}

/**
 * Create API config from runtime config
 */
export function createApiConfig(runtimeConfig) {
  return {
    baseUrl: runtimeConfig.api.baseUrl,
    timeout: runtimeConfig.api.timeout,
  };
}

/**
 * Check if development auth is enabled
 */
export function isDevelopmentAuth(runtimeConfig) {
  return runtimeConfig.auth.development;
}